import {
  Component,
  DestroyRef,
  effect,
  forwardRef,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  CustomFieldCompositionDto,
  CustomFieldFilterEntryDto,
  CustomFieldItemDto,
  EquipmentCustomFieldCollectionDetailDto,
  EquipmentItemDto,
  EquipmentService,
} from '@artemis-software/wr-api';
import { AbstractFormControl } from '@shared/form-controls/abstract-form-control';
import ValueTypeEnum = CustomFieldCompositionDto.ValueTypeEnum;
import { debounceTime } from 'rxjs';

export interface EquipmentFieldFilterField {
  field: CustomFieldItemDto;
  collectionTitle: string;
}

@Component({
  standalone: false,
  selector: 'wr-equipment-field-filter',
  templateUrl: './equipment-field-filter.component.html',
  styleUrls: ['./equipment-field-filter.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EquipmentFieldFilterComponent),
      multi: true,
    },
  ],
})
export class EquipmentFieldFilterComponent extends AbstractFormControl<CustomFieldFilterEntryDto[]> {
  private readonly equipmentService = inject(EquipmentService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  equipment = input<EquipmentItemDto | undefined>(undefined);

  fields: EquipmentFieldFilterField[] = [];
  fieldForm: FormGroup = this.fb.group({});

  readonly ValueTypeEnum = ValueTypeEnum;

  constructor() {
    super();
    effect(() => {
      const eq = this.equipment();
      this.resetFields();
      if (eq?.id) {
        this.equipmentService.findEquipmentCustomFieldCollectionByEquipmentId(eq.id).subscribe((collections) => {
          this.buildFields(collections);
        });
      }
    });
  }

  private resetFields(): void {
    this.fields = [];
    this.fieldForm = this.fb.group({});
    this.value = undefined;
  }

  private buildFields(collections: EquipmentCustomFieldCollectionDetailDto[]): void {
    this.fields = collections.flatMap((ec) =>
      ec.collection.members.map((m) => ({
        field: m.field,
        collectionTitle: ec.collection.title,
      })),
    );

    this.fieldForm = this.fb.group(
      Object.fromEntries(
        this.fields.map(({ field }) => [
          field.id,
          field.composition.valueType === ValueTypeEnum.Boolean
            ? this.fb.control<boolean | null>(null)
            : this.fb.control<string | number | null>(null),
        ]),
      ),
    );

    this.fieldForm.valueChanges
      .pipe(debounceTime(300),takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.emitFilters());
  }

  private emitFilters(): void {
    const filters: CustomFieldFilterEntryDto[] = [];

    for (const { field } of this.fields) {
      const rawValue = this.fieldForm.get(field.id)?.value;
      if (rawValue === null || rawValue === undefined || rawValue === '') {
        continue;
      }

      const entry: CustomFieldFilterEntryDto = { fieldId: field.id };

      switch (field.composition.valueType) {
        case ValueTypeEnum.String:
        case ValueTypeEnum.Longtext:
          entry.stringValue = String(rawValue);
          break;
        case ValueTypeEnum.Integer:
          entry.intValue = Number(rawValue);
          break;
        case ValueTypeEnum.Double:
          entry.doubleValue = Number(rawValue);
          break;
        case ValueTypeEnum.Boolean:
          entry.booleanValue = Boolean(rawValue);
          break;
      }

      filters.push(entry);
    }

    this.value = filters.length > 0 ? filters : undefined;
  }
}
